import React, { ReactNode, useCallback, useRef } from 'react';

interface FilterTriggerProps {
  /** "Type", "Client", "Created by" -- the facet, shown before the value. */
  label: string;
  /** The value differs from the default: filled pill. */
  active?: boolean;
  /** A Dropdown, SearchableClientDropdown or TextInput; the wrapper owns the chrome. */
  children: ReactNode;
  className?: string;
}

/**
 * A "Label · value" pill for the filter bar. It is the compact-dropdown scope
 * for filter bars, the way `.form-ui` is for fields: the six components that
 * render their own `.dropdown-trigger` are restyled by the wrapper class, not
 * by a prop on each.
 *
 * The whole pill is the hit area. A click on the label or the padding is
 * forwarded to the control inside, so the user need not aim at the value.
 */
const FilterTrigger: React.FC<FilterTriggerProps> = ({ label, active = false, children, className = '' }) => {
  const root = useRef<HTMLDivElement>(null);

  const forwardClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // The control handles its own clicks; only the label and padding forward.
    if (target.closest('.dropdown-container, input, button, select')) return;
    const control = root.current?.querySelector<HTMLElement>('.dropdown-trigger, input, select');
    if (!control) return;
    if (control instanceof HTMLInputElement) {
      control.focus();
      const picker = control as HTMLInputElement & { showPicker?: () => void };
      try {
        picker.showPicker?.();
      } catch {
        // showPicker throws when not user-activated or unsupported; focus is enough.
      }
    } else {
      control.click();
    }
  }, []);

  return (
    <div
      ref={root}
      className={`filter-trigger ${active ? 'is-active' : ''} ${className}`.trim()}
      onClick={forwardClick}
    >
      <span className="filter-trigger__label">{label}</span>
      {children}
    </div>
  );
};

export default FilterTrigger;
